import { Component, signal, OnInit, OnDestroy, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Router } from '@angular/router';
import { AIChatbotService, AIResponse, ChatContext } from '../../../core/services/ai/ai-chatbot.service';

interface ChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

@Component({
  selector: 'app-ai-chatbot',
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule
  ],
  templateUrl: './ai-chatbot.html',
  styleUrls: ['./ai-chatbot.scss']
})
export class AiChatbot implements OnInit, OnDestroy {
  @Output() actionRequested = new EventEmitter<{action: string, data?: any}>();

  // State signals
  isOpen = signal(false);
  messages = signal<ChatMessage[]>([]);
  isLoading = signal(false);
  currentMessage = '';

  // AI Context
  private chatContext: ChatContext = {
    messages: [],
    userContext: {
      currentPage: '',
      recentActions: [],
      userPreferences: {}
    }
  };

  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef,
    private aiService: AIChatbotService
  ) {}

  ngOnInit() {
    this.initializeChat();
    this.updateContext();
  }

  ngOnDestroy() {
    // Cleanup if needed
  }

  private initializeChat() {
    // Add welcome message
    this.messages.set([{
      id: '1',
      text: 'Hello! I\'m your AI assistant. How can I help you today?',
      isUser: false,
      timestamp: new Date()
    }]);
  }

  private updateContext() {
    this.chatContext.userContext.currentPage = this.router.url;
  }

  toggleChat() {
    this.isOpen.set(!this.isOpen());
  }

  async sendMessage() {
    const message = this.currentMessage.trim();
    if (!message || this.isLoading()) return;

    // Add user message
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      text: message,
      isUser: true,
      timestamp: new Date()
    };

    this.messages.update(messages => [...messages, userMessage]);
    this.currentMessage = '';
    this.isLoading.set(true);

    try {
      // Get AI response
      const aiResponse = await this.aiService.getAIResponse(message, this.chatContext, {
        useFallback: true,
        temperature: 0.7,
        maxTokens: 500
      });

      // Add AI response
      const aiMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: aiResponse.text,
        isUser: false,
        timestamp: new Date()
      };

      this.messages.update(messages => [...messages, aiMessage]);

      // Execute any actions if specified
      if (aiResponse.actions && aiResponse.actions.length > 0) {
        this.executeActions(aiResponse.actions);
      }

    } catch (error) {
      console.error('AI response error:', error);

      // Fallback response
      const fallbackMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: 'Sorry, I\'m having trouble processing your request. Please try again.',
        isUser: false,
        timestamp: new Date()
      };

      this.messages.update(messages => [...messages, fallbackMessage]);
    } finally {
      this.isLoading.set(false);
      this.updateContext();
    }
  }

  private executeActions(actions: string[]) {
    actions.forEach(action => {
      switch (action.toLowerCase()) {
        case 'open_dashboard':
          this.actionRequested.emit({ action: 'navigate', data: '/dashboard' });
          break;
        case 'open_projects':
          this.actionRequested.emit({ action: 'navigate', data: '/projects' });
          break;
        case 'open_tasks':
          this.actionRequested.emit({ action: 'navigate', data: '/tasks' });
          break;
        case 'add_project':
          this.actionRequested.emit({ action: 'open_form', data: 'add-project' });
          break;
        case 'add_task':
          this.actionRequested.emit({ action: 'open_form', data: 'add-task' });
          break;
        default:
          // Ignore unsupported actions from AI response.
          break;
      }
    });
  }

  onKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  clearChat() {
    this.messages.set([]);
    this.initializeChat();
  }
}
